/**
 * PagePagination
 *
 * Numbered (page-based) pagination: shows the current range and a
 * Previous / "Page X of Y" / Next control. For lists backed by an offset-based
 * API where any page can be loaded directly (e.g. the journal entries list).
 */

import { Button, Skeleton } from '@elsapiens/ui'

export interface PagePaginationProps {
  /** Current 1-based page number. */
  currentPage: number
  /** Total number of pages (>= 1). */
  totalPages: number
  /** Items per page (for the "showing X–Y" range). */
  pageSize: number
  /** Number of items rendered on the current page. */
  loadedCount: number
  /** Total count of items (from API, if available). */
  totalCount?: number
  /** Whether a page is currently loading. */
  isLoading?: boolean
  /** Show shimmer placeholder instead of real content. */
  shimmer?: boolean
  /** Callback to navigate to a specific page. */
  onPageChange: (page: number) => void
}

// Match the Table component's edge-to-edge pattern.
const edgeToEdgeStyle = {
  marginLeft: 'calc(var(--el-table-spacer-width) * -1)',
  marginRight: 'calc(var(--el-table-spacer-width) * -1)',
  marginBottom: 'calc(var(--el-table-spacer-width) * -1)',
  width: 'calc(100% + var(--el-table-spacer-width) * 2)',
  paddingLeft: 'var(--el-table-spacer-width)',
  paddingRight: 'var(--el-table-spacer-width)',
  paddingTop: 'var(--el-layout-gap-sm)',
  paddingBottom: 'var(--el-layout-gap-sm)',
}

export function PagePagination({
  currentPage,
  totalPages,
  pageSize,
  loadedCount,
  totalCount,
  isLoading = false,
  shimmer = false,
  onPageChange,
}: PagePaginationProps): JSX.Element | null {
  if (shimmer) {
    return (
      <div className="flex items-center justify-between border-t border-border" style={edgeToEdgeStyle}>
        <Skeleton height="1em" width="120px" />
      </div>
    )
  }

  if (loadedCount === 0 && totalPages <= 1) { return null }

  const start = loadedCount === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const end = (currentPage - 1) * pageSize + loadedCount

  return (
    <div className="flex items-center justify-between border-t border-border" style={edgeToEdgeStyle}>
      <span className="text-sm text-foreground-secondary">
        Showing {start}&ndash;{end}{(totalCount ?? 0) > 0 ? ` of ${totalCount}` : ''} items
      </span>
      <div className="flex items-center gap-3">
        <Button
          disabled={isLoading || currentPage <= 1}
          size="sm"
          variant="secondary"
          onClick={() => onPageChange(currentPage - 1)}
        >
          Previous
        </Button>
        <span className="text-sm text-foreground-secondary whitespace-nowrap">
          Page {currentPage} of {Math.max(totalPages, 1)}
        </span>
        <Button
          disabled={isLoading || currentPage >= totalPages}
          size="sm"
          variant="secondary"
          onClick={() => onPageChange(currentPage + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}
